import { useEffect, useState } from 'react';
import { RefreshCw, TrendingUp, Users, Wallet, Percent, Sparkles, AlertTriangle } from 'lucide-react';
import { api, type AdminOverview } from '../lib/api';

const brl = (n: number) =>
  n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 });
const pct = (n: number) => `${(n * 100).toFixed(1)}%`;

function Kpi({ label, value, sub, icon, tone }: { label: string; value: string; sub?: string; icon: React.ReactNode; tone?: 'good' | 'bad' | 'neutral' }) {
  const toneCls = tone === 'bad' ? 'text-red-500' : tone === 'good' ? 'text-emerald-500' : 'text-foreground';
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        {icon}{label}
      </div>
      <div className={`mt-2 text-2xl font-bold ${toneCls}`}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export function OwnerDashboard() {
  const [data, setData] = useState<AdminOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      setData(await api.getAdminOverview());
    } catch (e) {
      const status = (e as { status?: number })?.status;
      setError(status === 403 ? 'Acesso restrito ao dono da plataforma.' : 'Não foi possível carregar o painel.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-8 text-center text-muted-foreground">Carregando painel…</div>;
  if (error) return (
    <div className="p-8 text-center">
      <AlertTriangle className="mx-auto mb-3 text-muted-foreground" />
      <p className="text-muted-foreground">{error}</p>
    </div>
  );
  if (!data) return null;

  const maxSignup = Math.max(1, ...data.users.signupSeries.map((s) => s.count));
  const p = data.profit;

  return (
    <div className="mx-auto max-w-5xl p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl font-bold text-foreground">Painel do Dono</h1>
          <p className="text-xs text-muted-foreground">
            Gerado em {new Date(data.generatedAt).toLocaleString('pt-BR')}
          </p>
        </div>
        <button onClick={load} className="rounded-lg border border-border p-2 hover:bg-muted" title="Atualizar">
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="MRR" value={brl(data.revenue.mrrBrl)} sub={`ARR ${brl(data.revenue.arrBrl)}`} icon={<Wallet size={13} />} tone="good" />
        <Kpi label="Lucro/mês (est.)" value={brl(p.estimatedMonthlyProfitBrl)} sub={`margem ${pct(p.marginPct)}`} icon={<TrendingUp size={13} />} tone={p.estimatedMonthlyProfitBrl >= 0 ? 'good' : 'bad'} />
        <Kpi label="Pagantes" value={String(data.users.paying)} sub={`de ${data.users.total} contas`} icon={<Sparkles size={13} />} />
        <Kpi label="Conversão" value={pct(data.users.conversionRate)} sub="pagantes / total" icon={<Percent size={13} />} />
      </div>

      {/* Users row */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Usuários" value={String(data.users.total)} icon={<Users size={13} />} />
        <Kpi label="Novos este mês" value={String(data.users.newThisMonth)} sub={`mês anterior: ${data.users.newPrevMonth}`} icon={<Users size={13} />} tone={data.users.newThisMonth >= data.users.newPrevMonth ? 'good' : 'neutral'} />
        <Kpi label="Trials ativos" value={String(data.subscriptions.trialing)} sub="7 dias grátis" icon={<Sparkles size={13} />} />
        <Kpi label="Custo IA (mês)" value={brl(data.cost.aiCogsBrl)} sub={`US$ ${data.cost.aiCogsUsd.toFixed(2)} · ${data.cost.requestsThisMonth} req`} icon={<Wallet size={13} />} tone="bad" />
      </div>

      {/* Profit breakdown */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <h2 className="mb-3 text-sm font-semibold text-foreground">Lucro estimado (mês)</h2>
        <div className="space-y-1.5 text-sm">
          <Row label="Receita recorrente (MRR)" value={brl(p.mrrBrl)} tone="good" />
          <Row label="− Custo de IA (COGS, piso)" value={`− ${brl(p.cogsBrl)}`} tone="bad" />
          <Row label="− Taxas Asaas (est.)" value={`− ${brl(p.asaasFeesBrl)}`} tone="bad" />
          <div className="my-2 border-t border-border" />
          <Row label="= Lucro estimado" value={brl(p.estimatedMonthlyProfitBrl)} tone={p.estimatedMonthlyProfitBrl >= 0 ? 'good' : 'bad'} bold />
        </div>
        <p className="mt-3 flex items-start gap-1.5 text-[11px] text-muted-foreground">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          {data.assumptions.note} Câmbio US$1 = R${data.assumptions.brlPerUsd} · taxa Asaas {pct(data.assumptions.asaasFeeRate)}.
        </p>
      </div>

      {/* Signups chart */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <h2 className="mb-3 text-sm font-semibold text-foreground">Cadastros — últimos 30 dias</h2>
        {data.users.signupSeries.length === 0 ? (
          <p className="text-xs text-muted-foreground">Sem cadastros no período.</p>
        ) : (
          <div className="flex items-end gap-1 h-28">
            {data.users.signupSeries.map((s) => (
              <div key={s.day} className="flex-1 group relative flex flex-col items-center justify-end">
                <div className="w-full rounded-t bg-primary/70 hover:bg-primary transition-colors" style={{ height: `${(s.count / maxSignup) * 100}%` }} />
                <span className="pointer-events-none absolute -top-5 hidden rounded bg-popover px-1.5 py-0.5 text-[10px] text-foreground shadow group-hover:block">
                  {s.count} · {s.day.slice(5)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add-ons + subscriptions */}
      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-5">
          <h2 className="mb-3 text-sm font-semibold text-foreground">Add-ons ativos (MRR)</h2>
          {data.addons.length === 0 ? (
            <p className="text-xs text-muted-foreground">Nenhum add-on ativo ainda.</p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {data.addons.map((a, i) => (
                  <tr key={i} className="border-b border-border/50 last:border-0">
                    <td className="py-1.5 text-foreground">{a.addonId} <span className="text-muted-foreground">({a.cycle})</span></td>
                    <td className="py-1.5 text-right text-muted-foreground">×{a.count}</td>
                    <td className="py-1.5 text-right font-medium text-foreground">{brl(a.mrr)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <h2 className="mb-3 text-sm font-semibold text-foreground">Assinaturas por status</h2>
          <table className="w-full text-sm">
            <tbody>
              {Object.entries(data.subscriptions.byStatus).map(([status, count]) => (
                <tr key={status} className="border-b border-border/50 last:border-0">
                  <td className="py-1.5 text-foreground capitalize">{status}</td>
                  <td className="py-1.5 text-right font-medium text-foreground">{count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, tone, bold }: { label: string; value: string; tone?: 'good' | 'bad'; bold?: boolean }) {
  const toneCls = tone === 'bad' ? 'text-red-500' : tone === 'good' ? 'text-emerald-500' : 'text-foreground';
  return (
    <div className="flex items-center justify-between">
      <span className={`text-muted-foreground ${bold ? 'font-semibold text-foreground' : ''}`}>{label}</span>
      <span className={`${toneCls} ${bold ? 'font-bold' : 'font-medium'}`}>{value}</span>
    </div>
  );
}
